import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { motion } from '@/lib/motion';
import { useData } from '../data/DataProvider';
import { getSrcSet, SIZES } from '../utils/responsiveImage';

const API = process.env.REACT_APP_BACKEND_URL;
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'https://files.ihgbrands.com/ihg-ind';

// Helper to get full image URL
const getImageUrl = (path) => {
  if (!path) return `${MEDIA_URL}/generated/project_hotel_lobby.webp`;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads')) return `${API}${path}`;
  if (path.startsWith('/')) return `${API}${path}`;
  return `${MEDIA_URL}/generated/${path}`;
};

export const PortfolioSection = () => {
  const { projects } = useData();
  const featuredProjects = projects.slice(0, 4);

  return (
    <section id="portfolio" className="py-16 md:py-20 bg-gallery" data-testid="portfolio-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header - Centered */}
        <div className="text-center mb-10">
          <span className="label-architectural text-bronze">FEATURED WORK</span>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-obsidian mt-2">
            OUR PORTFOLIO
          </h2>
        </div>

        {/* Portfolio Grid - 2x2 */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={project.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <Link
                to={`/case-study/${project.slug || project.id || index + 1}`}
                className="group relative block aspect-[4/3] overflow-hidden bg-obsidian"
                data-testid={`project-card-${index}`}
              >
                <img loading="lazy" decoding="async"
                  src={getImageUrl(project.heroImage || project.image)} 
                  srcSet={getSrcSet(project.heroImage || project.image)}
                  sizes={SIZES.threeCol}
                  alt={project.title}
                  width={800}
                  height={600}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 group-hover:opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-end">
                  <span className="font-body text-[10px] text-white/50 uppercase tracking-wider mb-1">
                    {project.location}
                  </span>
                  <h3 className="font-display text-sm md:text-base font-bold text-white group-hover:text-bronze transition-colors">
                    {project.title.toUpperCase()}
                  </h3>
                </div>
                <div className="absolute top-3 right-3 md:top-4 md:right-4 w-8 h-8 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity group-hover:bg-white">
                  <ArrowUpRight weight="bold" className="w-3 h-3 text-white group-hover:text-obsidian" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 font-body text-xs font-semibold text-obsidian uppercase tracking-wider hover:text-bronze transition-colors"
            data-testid="portfolio-view-all"
          >
            VIEW ALL PROJECTS
            <ArrowRight weight="bold" className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
